import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { loadContactFilePresignedUrls } from '../../contact-file/actions/load-contact-file-presigned-urls';
import { contactFilesWithPresignedUrlsForIdsSelector } from '../../contact-file/contact-file-selectors';

import { EmailMessage } from './email-message';
import { TextMessage } from './text-message';

import { getSenderName } from '../../util/get-sender-name';
import { isTruthy } from '../../util/is-truthy';
import { MESSAGE_TYPE } from '../../util/message-types';

type Props = {
  messages: Array<Message>;
  name: SimpleName;
  orgName: string;
  orgId?: number;
  urlVisitDetails?: { [pathLowerCased: string]: VisitDetail };
};

export const LegacyMessageList = (props: Props) => {
  const { messages, name, orgName, orgId, urlVisitDetails } = props;

  const contactFileIds = messages
    .map((m) => m?.ContactFiles?.map((contactFile) => contactFile.contactFileId))
    .filter(isTruthy)
    .flat();
  const dispatch = useDispatch();

  useEffect(() => {
    if (contactFileIds.length) {
      dispatch(loadContactFilePresignedUrls(contactFileIds));
    }
  });

  const contactFilesWithUrls = useSelector((state: any) =>
    contactFilesWithPresignedUrlsForIdsSelector(state, contactFileIds),
  );

  return (
    <div className="u-display-flex u-flexDirection-column">
      {messages.map((m) => {
        const containerStyle = {
          width: '85%',
          alignSelf: m.isInbound ? 'flex-start' : 'flex-end',
        };

        const contactFilesWithUrlsForMessage = contactFilesWithUrls?.filter(
          (
            contactFileWithPresignedUrl,
          ): contactFileWithPresignedUrl is {
            contactFile: ContactFile;
            presignedUrl: string;
            expiresAt: string;
          } =>
            isTruthy(contactFileWithPresignedUrl.contactFile) &&
            contactFileWithPresignedUrl.contactFile.messageId === m?.messageId,
        );

        return (
          <div key={m.messageId} style={containerStyle}>
            {m.type === MESSAGE_TYPE.EMAIL ? (
              <EmailMessage
                senderName={getSenderName(m.activity, name, orgName)}
                recipientName={m.isInbound ? orgName : `${name.firstName} ${name.lastName}`}
                sendDateTime={m.activity.datetime}
                messageBody={m.messageBody}
                subject={m.subject || ''}
                announcement={m.Announcement}
                isInbound={m.isInbound}
                isAutomated={m.activity.isAutomated}
                isFailed={m.isFailed}
                urlVisitDetails={urlVisitDetails}
                allowLinkToAnnouncement
                contactFilesWithUrls={contactFilesWithUrlsForMessage}
                dataCy={`message-${m.messageId}`}
                messageIdAndLanguageCode={{
                  messageId: m.messageId,
                  messageLanguageCode: m.languageCode,
                }}
              />
            ) : (
              <TextMessage
                messageSid={m.messageSid}
                senderName={getSenderName(m.activity, name, orgName)}
                sendDateTime={m.activity.datetime}
                messageBody={m.messageBody}
                announcement={m.Announcement}
                isInbound={m.isInbound}
                isAutomated={m.activity.isAutomated}
                imageUrls={m.imageUrls}
                isFailed={m.isFailed}
                urlVisitDetails={urlVisitDetails}
                orgId={orgId}
                allowLinkToAnnouncement
                contactFilesWithUrls={contactFilesWithUrlsForMessage}
                dataCy={`message-${m.messageId}`}
                messageIdAndLanguageCode={{
                  messageId: m.messageId,
                  messageLanguageCode: m.languageCode,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
